import { DestinationItem } from '@tableau/nested-project-explorer';
import {
  ButtonType,
  ButtonWidget,
  InlineAlertType,
  InlineAlertWidget,
  TextAreaWidget,
  TextFieldWidget
} from '@tableau/widgets';
import { FormikProps, withFormik } from 'formik';
import React from 'react';
import _ from 'underscore';
import ReactDOM = require('react-dom');
import { characterCounter } from '~src/commonInfra/utils/CharacterCounter';
import { GetFlowsRequest, GetFlowsResult } from '~src/flow/serverApi/requests/GetFlowsRequest';

import { Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../commonInfra/components/ButtonWithSpinner';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { FileInput, FileInputProps } from '../../userManagement/components/FileInput';
import { UploadWorkbookStyles } from '../styles/UploadWorkbookStyles';
import { getContentsCall, getFileNameOnly, isValidExtension } from '../utils/UploadContentDialogHelper';
import { sendUploadFlowDialogTelemetry } from '../utils/UploadFlowTelemetryEvents';
import { UploadFlowDialogOpts } from './ConnectedUploadFlowDialog';
import { preventDefaultDrag } from './FileDragAndDrop';
import { ProjectExplorerDropdown } from './form/ProjectExplorerDropdown';
import { OverwriteConfirmDialogOpts } from './OverwriteConfirmDialog';

export interface UploadFlowDialogValues {
  file: File;
  name: string;
  projectId: string;
  description: string;
  nameLength?: string;
}

export interface UploadFlowDispatchProps {
  uploadFlowActionDispatch: (values: UploadFlowDialogValues, overwrite?: boolean) => void;
}

export interface UploadFlowExternalProps {
  snapshot?: UploadFlowDialogValues;
  parentElement?: React.RefObject<any>;
  close: () => void;
}

interface InternalUploadFlowDialogFormState {
  readonly isBusy: boolean;
  readonly willOverwriteFlow: boolean;
}

export type UploadFlowDialogProps = UploadFlowDispatchProps & UploadFlowExternalProps;
export type InternalUploadFlowDialogFormProps = UploadFlowDialogProps & FormikProps<UploadFlowDialogValues>;

const MAX_NAME_LENGTH = 251;
const MAX_DESCRIPTION_LENGTH = 4000;
const MAX_FILE_SIZE_BYTES = 52428800;
const VALID_FILE_TYPES = ['.tfl', '.tflx'];
const KEYPRESS_DEBOUNCE_MS = 250;

export const UploadFlowDialogVariables = {
  MAX_NAME_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  MAX_FILE_SIZE_BYTES,
  KEYPRESS_DEBOUNCE_MS
};

export enum UploadFlowErrorEvent {
  ExceedMaxFileSize = 'ExceedMaxFileSize',
  UnsupportedFileExt = 'UnsupportedFileExtension',
  NoError = ''
}

export class InternalUploadFlowDialogForm extends React.Component<InternalUploadFlowDialogFormProps, InternalUploadFlowDialogFormState> {
  private fileInput: React.RefObject<HTMLInputElement>;
  private fileButtonLegacyRef: HTMLElement;
  private latestGetFlowsRequestTimestamp: number = 0;
  private validateFlowPromise: Promise<GetFlowsResult>;
  private isComponentMounted: boolean = true;

  constructor(props: InternalUploadFlowDialogFormProps) {
    super(props);
    this.fileInput = React.createRef();
    this.props.validateForm();
    this.state = {
      isBusy: false,
      willOverwriteFlow: false,
    };
  }

  public shouldComponentUpdate(nextProps: InternalUploadFlowDialogFormProps, nextState: InternalUploadFlowDialogFormState) {
    return !(_.isEqual(this.props.values, nextProps.values) && _.isEqual(this.props.errors, nextProps.errors)
      && _.isEqual(this.state, nextState));
  }

  public componentWillUnmount() {
    this.isComponentMounted = false;

    if (this.props.parentElement) {
      this.props.parentElement.current.focus();
    }
  }

  public componentDidMount() {
    setTimeout(() => this.fileButtonLegacyRef.focus(), 10);
  }

  private triggerFileInput = () => {
    this.fileInput.current.click();
  };

  private setFileButton = (component: ButtonWidget) => {
    this.fileButtonLegacyRef = ReactDOM.findDOMNode(component) as HTMLElement;
  };

  private handleFileSelection: FileInputProps['handleFileInput'] = (file, source) => {
    if (file) {
      const name = getFileNameOnly(file.name);
      this.validateFlowName(name);
      this.props.setValues({
        ...this.props.values,
        file,
        name
      });
      let errorEvent = UploadFlowErrorEvent.NoError;
      if (file?.size > MAX_FILE_SIZE_BYTES) {
        errorEvent = UploadFlowErrorEvent.ExceedMaxFileSize;
      }
      if (!isValidExtension(file.name, VALID_FILE_TYPES)) {
        errorEvent = UploadFlowErrorEvent.UnsupportedFileExt;
      }
      sendUploadFlowDialogTelemetry(source, file, errorEvent);
    }
  };

  private getDescriptionText = () => characterCounter({value: this.props.values.description, maximum: MAX_DESCRIPTION_LENGTH});

  private handleFlowOverwriteWithConfirmation() {
    ReactModalService.openCustom(OverwriteConfirmDialogOpts({
      title: Messages.contentUpload_overwriteFlow(),
      warningMessage: Messages.contentUpload_confirmFlowOverwriteWarning({FLOW_NAME: this.props.values.name}),
      onSubmit: () => {
        const overwrite = true;
        this.props.uploadFlowActionDispatch(this.props.values, overwrite);
      },
      onCancel: () => {
        ReactModalService.openCustom(UploadFlowDialogOpts(this.props.values));
      }
    }, 'upload-flow-overwrite-confirmation'));
  }

  private handleSubmit = () => {
    if (this.validateFlowPromise) {
      this.setState({ isBusy: true });
    }
    this.validateFlowPromise?.then((result: GetFlowsResult) => {
      if (!this.isComponentMounted) {
        return;
      }
      if (result?.totalCount > 0) {
        this.handleFlowOverwriteWithConfirmation();
      } else {
        this.props.uploadFlowActionDispatch(this.props.values);
      }
      this.props.close();
    });
  };

  private handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.handleChange(event);
    this.validateFlowName(event.currentTarget.value);
  };

  private validateFlowName = _.debounce((flowName: string, projectId: string = this.props.values.projectId) => {
    if (flowName && projectId) {
      const requestTimestamp = Date.now();
      this.latestGetFlowsRequestTimestamp = requestTimestamp;

      this.validateFlowPromise = getContentsCall(flowName, projectId, GetFlowsRequest);
      this.validateFlowPromise.then(result => {
        let willOverwriteFlow = false;
        if (result?.totalCount > 0 && requestTimestamp === this.latestGetFlowsRequestTimestamp) {
          willOverwriteFlow = true;
        }
        if (this.isComponentMounted) {
          this.setState({ willOverwriteFlow });
        }
      });
    } else {
      this.setState({ willOverwriteFlow: false });
    }
  }, KEYPRESS_DEBOUNCE_MS);

  render() {
    return (
      <>
        <div className={UploadWorkbookStyles.contentContainer}>
          <div className={UploadWorkbookStyles.innerContentContainer}>
            <FileInput
              accept={VALID_FILE_TYPES.join(',')}
              activeStateClassName={UploadWorkbookStyles.activeOutline}
              allowFileDrop={true}
              containerClassName={UploadWorkbookStyles.dragAndDropContainer}
              data-tb-test-id="upload-flow-input-file-id"
              defaultStateClassName={UploadWorkbookStyles.defaultOutline}
              handleFileInput={this.handleFileSelection}
              name="file"
              onDragClassName={UploadWorkbookStyles.focusOutline}
              ref={this.fileInput}
              testId="upload-flow-dialog-file"
            >
              <div className={UploadWorkbookStyles.dragAndDropChildren} onDragLeave={preventDefaultDrag}>
                {this.props.values.file &&
                  <b className={UploadWorkbookStyles.fileName}>{this.props.values.file.name}</b>
                }
                {!this.props.values.file &&
                  <>
                    <p className={UploadWorkbookStyles.dragAndDropText}>{Messages.contentUpload_dragAndDrop()}</p>
                    <p className={UploadWorkbookStyles.dragAndDropOrText}>{Messages.contentUpload_dragAndDropOr()}</p>
                  </>
                }
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  testId="upload-flow-dialog-file"
                  children={Messages.contentUpload_chooseAFile()}
                  handleClick={this.triggerFileInput}
                  ref={this.setFileButton}
                  aria-describedby="upload-flow-dialog-flow-limit-message"
                />
              </div>
            </FileInput>
            {this.props.errors.file && <InlineAlertWidget children={this.props.errors.file as string} testId="flow-invalid-extension-alert" type={InlineAlertType.Error} />}
            <p className={UploadWorkbookStyles.uploadWorkbookFileLimitDescription} id="upload-flow-dialog-flow-limit-message">
              {Messages.contentUpload_uploadFlowLimitMessage()}
            </p>
          </div>
          <div className={UploadWorkbookStyles.divider}/>
          <div className={`${UploadWorkbookStyles.workbookDetailsContainer} ${UploadWorkbookStyles.innerContentContainer}`}>
            <TextFieldWidget
              kind="line"
              name="name"
              testId="upload-flow-dialog-name"
              value={this.props.values.name}
              label={Messages.name()}
              className={UploadWorkbookStyles.textField}
              onChange={this.handleNameChange}
              onClear={() => {
                this.props.setFieldValue('name', '');
                this.setState({ willOverwriteFlow: false });
              }}
              message={this.props.errors.nameLength}
              valid={this.props.errors.nameLength ? false : undefined}
            />
            {this.state.willOverwriteFlow && <InlineAlertWidget children={Messages.contentUpload_flowNameOverwriteWarningMessage()} testId="flow-overwrite-alert" type={InlineAlertType.Warning} />}

            <ProjectExplorerDropdown
              testId="upload-flow-dialog-project"
              initialProjectId={this.props.values.projectId}
              handleProjectSelect={(project: DestinationItem) => {
                const projectId = project?.id || null;
                this.props.setFieldValue('projectId', projectId);
                this.validateFlowName(this.props.values.name, projectId);
              }}
            />
            <div>
              <TextAreaWidget
                name="description"
                label={Messages.contentUpload_uploadFlowDescriptionTitle()}
                testId="upload-flow-dialog-description"
                value={this.props.values.description}
                className={`${UploadWorkbookStyles.textField} ${UploadWorkbookStyles.descriptionField}`}
                onChange={this.props.handleChange}
                valid={this.props.errors.description ? false : undefined}
                message={this.getDescriptionText()}
              />
            </div>
          </div>
        </div>
        <div className={UploadWorkbookStyles.footer}>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={'upload-flow-dialog-cancel'}
            children={Messages.cancel()}
            handleClick={this.props.close}
          />
          <ButtonWithSpinner
            disabled={!this.props.isValid}
            buttonType={ButtonType.Primary}
            showSpinner={this.state.isBusy}
            testId={'upload-flow-dialog-submit'}
            children={Messages.upload()}
            onClick={this.handleSubmit}
          />
        </div>
      </>
    );
  }
}

function validateFormFields(values: UploadFlowDialogValues) {
  const errors: any = {};

  if (!values.name) {
    errors.nullName = 'no name';
  }

  if (values.name?.length > MAX_NAME_LENGTH) {
    errors.nameLength = characterCounter({ value: values.name, maximum: MAX_NAME_LENGTH });
  }

  if (!values.file) {
    errors.nullFile = 'no file';
  } else {
    if (values.file?.size > MAX_FILE_SIZE_BYTES) {
      errors.file = Messages.contentUpload_flowFileLimitError();
    }
    if (!isValidExtension(values.file.name, VALID_FILE_TYPES)) {
      errors.file = Messages.contentUpload_flowIncorrectExtensionError();
    }
  }

  if (!values.projectId) {
    errors.nullProjectId = 'no project id';
  }

  if (values.description?.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = 'invalid description';
  }
  return errors;
}

export const UploadFlowDialog = withFormik<UploadFlowDialogProps, UploadFlowDialogValues>({
  mapPropsToValues: (props) => {
    return props.snapshot ?? {
      file: null,
      name: '',
      projectId: null,
      description: '',
    };
  },
  validate: (values) => {
    return validateFormFields(values);
  },
  handleSubmit: (values, actions) => {}
})(InternalUploadFlowDialogForm);
